<mat-sidenav-container class="chat-shell" autosize>
  <!-- ===== HISTORY SIDE MENU ===== -->
  <mat-sidenav class="chat-history" mode="side" [opened]="aiService.historyOpen()" disableClose>
    <button mat-flat-button type="button" class="chat-history__new" (click)="newChat()">
      <mat-icon>add</mat-icon>
      {{ '$ai.chat.new-chat' | translateAsync }}
    </button>

    <div class="chat-history__label">{{ '$ai.chat.recent' | translateAsync }}</div>

    <div class="chat-history__list">
      @for (conv of aiService.conversations(); track conv.id) {
        <button
          type="button"
          class="conv-item"
          [class.conv-item--active]="isActiveConversation(conv)"
          (click)="openConversation(conv)"
          [matTooltip]="conv.title"
          matTooltipShowDelay="500"
        >
          <mat-icon>chat_bubble_outline</mat-icon>
          <span class="conv-item__text">
            <span class="conv-item__title">{{ conv.title }}</span>
            <span class="conv-item__meta">
              {{ conv.model ?? ('$ai.chat.default-model' | translateAsync) }} ·
              {{ thinkingLabel(conv.thinkingLevel) | translateAsync }} ·
              {{ formatConversationDate(conv.updatedAt) }}
            </span>
          </span>
        </button>
      } @empty {
        <div class="chat-history__empty">{{ '$ai.chat.no-conversations' | translateAsync }}</div>
      }
    </div>
  </mat-sidenav>

  <!-- ===== MAIN CHAT ===== -->
  <mat-sidenav-content class="chat-main" [ngClass]="'chat-main--' + persona()">
    <div class="chat-header">
      <button
        mat-icon-button
        type="button"
        class="chat-header__menu"
        [matTooltip]="'$ai.chat.toggle-history' | translateAsync"
        (click)="toggleHistory()"
      >
        <mat-icon>menu</mat-icon>
      </button>
      <span class="chat-avatar"><mat-icon>{{ personaIcon() }}</mat-icon></span>
      <div class="chat-header__titles">
        <span class="chat-title">{{ '$ai.persona.' + persona() + '.title' | translateAsync }}</span>
        <span class="chat-subtitle">{{ '$ai.persona.' + persona() + '.subtitle' | translateAsync }}</span>
      </div>
      @if (showModelPicker() && aiService.availableModels().length) {
        <button
          mat-button
          type="button"
          class="chat-header__model"
          [matMenuTriggerFor]="modelMenu"
          [matTooltip]="'$ai.chat.model-tooltip' | translateAsync"
        >
          <span class="chat-header__model-label">{{ selectedModelLabel() }}</span>
          <mat-icon>expand_more</mat-icon>
        </button>
        <mat-menu #modelMenu="matMenu" xPosition="before">
          @for (model of aiService.availableModels(); track model.id) {
            <button
              mat-menu-item
              type="button"
              class="chat-model-option"
              [class.chat-model-option--active]="model.id === aiService.selectedModel()"
              (click)="selectModel(model.id)"
            >
              <span class="chat-model-option__name">{{ model.label }}</span>
              @if (modelMeta(model); as meta) {
                <span class="chat-model-option__meta">{{ meta }}</span>
              }
            </button>
          }
        </mat-menu>
        <button
          mat-button
          type="button"
          class="chat-header__thinking"
          [matMenuTriggerFor]="thinkingMenu"
          [matTooltip]="'$ai.chat.thinking-tooltip' | translateAsync"
        >
          <mat-icon>psychology</mat-icon>
          <span class="chat-header__thinking-label">{{
            selectedThinkingLabel() | translateAsync
          }}</span>
          <mat-icon>expand_more</mat-icon>
        </button>
        <mat-menu #thinkingMenu="matMenu" xPosition="before">
          <!-- First option hands the decision back to the provider; the rest are the levels the server offers. -->
          <button
            mat-menu-item
            type="button"
            class="chat-thinking-option"
            [class.chat-thinking-option--active]="aiService.selectedThinkingLevel() === null"
            (click)="selectThinkingLevel(null)"
          >
            <span class="chat-thinking-option__name">{{
              thinkingLabel(null) | translateAsync
            }}</span>
          </button>
          @for (level of aiService.thinkingLevels(); track level) {
            <button
              mat-menu-item
              type="button"
              class="chat-thinking-option"
              [class.chat-thinking-option--active]="level === aiService.selectedThinkingLevel()"
              (click)="selectThinkingLevel(level)"
            >
              <span class="chat-thinking-option__name">{{
                thinkingLabel(level) | translateAsync
              }}</span>
            </button>
          }
        </mat-menu>
      }
      @if (canRateSession()) {
        <!-- Optional and out of the way: one icon, the stars only in its menu. -->
        <button
          mat-icon-button
          type="button"
          class="chat-header__action session-rating__trigger"
          data-testid="session-rating-trigger"
          [matMenuTriggerFor]="ratingMenu"
          [matTooltip]="'$ai.chat.rate-session' | translateAsync"
          [attr.aria-label]="'$ai.chat.rate-session' | translateAsync"
        >
          <mat-icon class="session-rating__icon" [class.session-rating__icon--on]="aiService.sessionRating() !== null">star</mat-icon>
          @if (aiService.sessionRating(); as rating) {
            <span class="session-rating__value">{{ rating }}</span>
          }
        </button>
        <mat-menu #ratingMenu="matMenu" class="session-rating-menu" xPosition="before">
          <div class="session-rating">
            <ng-container *ngTemplateOutlet="starsTpl"></ng-container>
          </div>
        </mat-menu>
      }
      <button
        mat-icon-button
        type="button"
        class="chat-header__action"
        [matTooltip]="'$ai.chat.new-chat' | translateAsync"
        (click)="newChat()"
      >
        <mat-icon>edit_square</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        [matTooltip]="'$ai.chat.clear' | translateAsync"
        (click)="clearChat()"
        [disabled]="aiService.messages().length === 0 || !aiService.conversationId()"
      >
        <mat-icon>delete_sweep</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        class="chat-header__close"
        [matTooltip]="'$ai.global.close' | translateAsync"
        [attr.aria-label]="'$ai.global.close' | translateAsync"
        (click)="global.close()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="message-list" #messageList>
      @for (msg of aiService.messages(); track msg.id) {
        @if (!isThinkingMessage(msg) && !isToolMessage(msg)) {
          <div
            class="message"
            [class.message--user]="isUserMessage(msg)"
            [class.message--assistant]="!isUserMessage(msg)"
            [ngClass]="isUserMessage(msg) ? '' : 'message--agent-' + agentOf(msg)"
          >
            @if (!isUserMessage(msg)) {
              <ng-container *ngTemplateOutlet="agentTpl; context: { $implicit: agentOf(msg) }"></ng-container>
            }
            <div
              class="message__bubble"
              [class.message__bubble--redirecting]="global.redirecting() === msg.id"
              [attr.aria-busy]="global.redirecting() === msg.id ? 'true' : null"
            >
              <wbi-markdown class="message__text" [markdown]="msg.content"></wbi-markdown>

              @if (msg.queryCandidates?.length) {
                <div class="message__query-candidates">
                  <span class="message__candidates-label">{{
                    '$ai.chat.found-queries' | translateAsync
                  }}</span>
                  <ul class="candidate-list">
                    @for (c of msg.queryCandidates; track c.id) {
                      <li>
                        <strong>{{ c.name }}</strong>
                        <span class="candidate-cols">[{{ c.columns.join(', ') }}]</span>
                      </li>
                    }
                  </ul>
                </div>
              }

              @if (msg.guideLinks?.length) {
                <div class="message__guide-links">
                  @for (link of msg.guideLinks; track link.dashboardId + (link.blockId ?? '')) {
                    <button mat-stroked-button type="button" (click)="global.openGuideLink(link)">
                      <mat-icon fontSet="hgi" [fontIcon]="link.blockId ? 'hgi-dashboard-square-01' : 'hgi-dashboard-browsing'"></mat-icon>
                      @if (link.blockName) {
                        {{ link.blockName }} <span class="message__guide-link-dashboard">· {{ link.dashboardName }}</span>
                      } @else {
                        {{ link.dashboardName }}
                      }
                    </button>
                  }
                </div>
              }

              @if (msg.savedQuery) {
                <div class="message__saved-query">
                  <mat-icon inline>save</mat-icon>
                  <span
                    >{{ '$ai.chat.query-auto-saved' | translateAsync }}
                    <strong>{{ msg.savedQuery!.name }}</strong></span
                  >
                </div>
              }

              @if (msg.alertProposal; as proposal) {
                <!-- WB-222: the alerts agent proposes; nothing is saved until the user confirms here. -->
                <wbi-alert-proposal-card
                  [proposal]="proposal"
                  [state]="global.alertCard(msg.id)"
                  [canConfirm]="global.canManageAlerts()"
                  (confirmed)="global.confirmAlert(msg.id)"
                  (dismissed)="global.dismissAlert(msg.id)"
                ></wbi-alert-proposal-card>
              }

              @if (msg.composerQuestionnaire; as questionnaire) {
                <!-- WB-177 phase 5: the composer asks before assuming; answered here, once. -->
                <wbi-composer-questionnaire
                  [clarify]="questionnaire.clarify"
                  [purpose]="questionnaire.clarifyFor ?? 'dashboard'"
                  [disabled]="global.isQuestionnaireAnswered(msg.id) || global.busy()"
                  (answered)="answerQuestionnaire(msg, $event)"
                ></wbi-composer-questionnaire>
              }

              @if (msg.suggestions?.length) {
                <div class="message__suggestions">
                  <span class="message__suggestions-label">{{
                    '$ai.chat.suggestions-label' | translateAsync
                  }}</span>
                  <div class="suggestion-chips">
                    @for (s of msg.suggestions; track s) {
                      <button
                        mat-stroked-button
                        type="button"
                        (click)="useSuggestion(s)"
                        [disabled]="global.busy()"
                      >
                        {{ s }}
                      </button>
                    }
                  </div>
                </div>
              }

              @if (!isUserMessage(msg) && msg.meta) {
                <div class="message__meta">
                  <mat-icon class="meta-icon">{{
                    isLocalModel(msg.meta.model) ? 'computer' : 'cloud'
                  }}</mat-icon>
                  <span class="meta-model">{{ msg.meta.model }}</span>
                  <span class="meta-separator">·</span>
                  <span class="meta-time">{{ formatResponseTime(msg.meta.responseTimeMs) }}</span>
                  @if (msg.meta.usage; as usage) {
                    <span class="meta-separator">·</span>
                    <span
                      class="meta-tokens"
                      [class.meta-tokens--priced]="canSeeCost() && msg.meta.costUsd !== undefined"
                      [matTooltip]="
                        ('$ai.chat.turn-cost' | translateAsync) + ': ' + formatCost(msg.meta.costUsd ?? 0) +
                        ' · ' + usage.promptTokens + ' ' + ('$ai.chat.tokens-in' | translateAsync) +
                        ' / ' + usage.completionTokens + ' ' + ('$ai.chat.tokens-out' | translateAsync) +
                        ' · ' + (msg.meta.llmCalls ?? 1) + ' ' + ('$ai.chat.llm-calls' | translateAsync)
                      "
                      [matTooltipDisabled]="!canSeeCost() || msg.meta.costUsd === undefined"
                      >{{ usage.totalTokens }} tok</span
                    >
                  }
                </div>
              }
            </div>
          </div>
        }
      }

      @if (global.routing()) {
        <div class="message message--assistant" [ngClass]="'message--agent-' + global.routingAgent()">
          <ng-container *ngTemplateOutlet="agentTpl; context: { $implicit: global.routingAgent() }"></ng-container>
          @if (global.streamingReply()) {
            <div class="message__bubble">
              <wbi-markdown class="message__text" [markdown]="global.streamingReply()"></wbi-markdown>
              <span class="typing-cursor" aria-hidden="true">▋</span>
            </div>
          } @else {
            <div class="message__bubble message__bubble--thinking">
              <mat-spinner diameter="16"></mat-spinner>
              <span>{{ global.routingStatus().key | translateAsync: global.routingStatus().params }}</span>
            </div>
          }
        </div>
      }

      @if (aiService.loading()) {
        <div class="message message--assistant message--agent-builder">
          <ng-container *ngTemplateOutlet="agentTpl; context: { $implicit: 'builder' }"></ng-container>
          @if (aiService.streamingContent()) {
            <div class="message__bubble">
              <wbi-markdown
                class="message__text"
                [markdown]="aiService.streamingContent()"
              ></wbi-markdown>
              <span class="typing-cursor" aria-hidden="true">▋</span>
            </div>
          } @else {
            <div class="message__bubble message__bubble--thinking">
              <mat-spinner diameter="16"></mat-spinner>
              <span>{{ aiService.thinkingStepKey() | translateAsync }}</span>
            </div>
          }
        </div>
      }

      @if (aiService.failedMessage()) {
        <div class="message message--user message--failed">
          <div class="message__bubble">
            <span class="message__text">{{ aiService.failedMessage() }}</span>
            <div class="message__failed-actions">
              <mat-icon class="failed-icon">error_outline</mat-icon>
              <span class="failed-label">{{
                '$ai.chat.error-code.' + (aiService.failedErrorCode() || 'internal')
                  | translateAsync
              }}</span>
              @if (aiService.failedErrorDetail(); as detail) {
                <button
                  mat-icon-button
                  type="button"
                  class="failed-info"
                  [matTooltip]="detail"
                  matTooltipClass="failed-info-tooltip"
                  [attr.aria-label]="'$ai.chat.error-detail' | translateAsync"
                >
                  <mat-icon>info</mat-icon>
                </button>
              }
              <button mat-stroked-button type="button" (click)="retry()">
                <mat-icon>refresh</mat-icon>
                {{ '$ai.chat.retry' | translateAsync }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>

    <div class="chat-composer">
      <p class="chat-disclaimer">
        {{ '$ai.persona.' + persona() + '.disclaimer' | translateAsync }}
        <!-- Which query path the turns are running on. Only for whoever may pick the model: it is
             an internal switch of the benchmark, read from the server's environment. -->
        @if (canSelectModel()) {
          <span class="chat-disclaimer__path">
            ·
            {{
              (aiService.sqlFirst() ? '$ai.chat.path-sql' : '$ai.chat.path-ast') | translateAsync
            }}
          </span>
        }
      </p>
      <div class="chat-input">
        <mat-form-field appearance="outline" class="chat-input__field" subscriptSizing="dynamic">
          <textarea
            matInput
            rows="2"
            [formControl]="inputControl"
            [placeholder]="'$ai.persona.' + persona() + '.placeholder' | translateAsync"
            (keydown)="onKeydown($event)"
          ></textarea>
        </mat-form-field>
        <!-- While a turn is in flight the same button stops it: the wait is the only moment the
             stop makes sense, and a second button beside it would be dead weight the rest of the
             time. -->
        @if (aiService.loading() || global.routing()) {
          <button
            mat-flat-button
            type="button"
            class="chat-send chat-send--stop"
            [attr.aria-label]="'$ai.chat.stop' | translateAsync"
            [matTooltip]="'$ai.chat.stop' | translateAsync"
            (click)="stop()"
          >
            <mat-icon>stop</mat-icon>
          </button>
        } @else {
          <button
            mat-flat-button
            type="button"
            class="chat-send"
            [attr.aria-label]="'$ai.chat.send' | translateAsync"
            (click)="send()"
            [disabled]="!inputControl.value.trim() || global.busy()"
          >
            <mat-icon>send</mat-icon>
          </button>
        }
      </div>
      <div class="chat-input__hint">{{ '$ai.chat.input-hint' | translateAsync }}</div>
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>

<!-- The five stars of the session rating menu. -->
<ng-template #starsTpl>
  @for (star of stars; track star) {
    <button
      mat-icon-button
      type="button"
      class="session-rating__star"
      data-testid="session-star"
      [attr.aria-label]="star + ' / 5'"
      [attr.aria-pressed]="(aiService.sessionRating() ?? 0) >= star"
      (click)="rateSession(star)"
    >
      <!-- Material Symbols: one glyph, filled through its FILL axis. `star_border` is not a symbol. -->
      <mat-icon
        class="session-rating__icon"
        [class.session-rating__icon--on]="(aiService.sessionRating() ?? 0) >= star"
        >star</mat-icon
      >
    </button>
  }
</ng-template>

<!-- The face of the agent that wrote a reply: its icon on its colour, named for screen readers. -->
<ng-template #agentTpl let-agent>
  <span
    class="message__agent"
    role="img"
    [attr.aria-label]="'$ai.agent.' + agent | translateAsync"
    [matTooltip]="'$ai.agent.' + agent | translateAsync"
    matTooltipPosition="left"
  >
    <mat-icon>{{ agentIcon(agent) }}</mat-icon>
  </span>
</ng-template>
